import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, useMediaQuery, CircularProgress, IconButton } from "@mui/material";
import { GoogleMap, Circle, InfoWindow, OverlayView } from "@react-google-maps/api";
import CustomButton from "../../../../components/CustomButton";
import { BASE_URL } from "../../../../components/BaseUrl";
import { decryptToken } from "../../../../Utlis/Encryption";
import { useTranslation } from "react-i18next";
import maparrow from "../../../../assets/maparrow.svg";
import CloseIcon from "@mui/icons-material/Close";
import CustomText, { textStyles } from "../../../../components/CustomText";
import { convertUTCToLocalDateTime } from "../../../../components/dateTimeUtils";

const defaultCenter = { lat: 39.8283, lng: -98.5795 };
const defaultZoom = 5;
const focusedZoom = 13;

const statusColors = {
  assigned: "#006EC2",
  in_progress: "#FF8C00",
  completed: "#00C48C",
  delayed: "#FF0000",
  overdue: "#FF0000",
  not_done: "#000000",
  cancelled: "#808080",
  canceled: "#808080",
};

const TaskMarkerContent = ({ workerName, status }) => {
  const statusColor = statusColors[status] || statusColors.assigned;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        cursor: "pointer",
        transition: "transform 0.2s ease",
        "&:hover": {
          transform: "scale(1.05)",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: 20,
          height: 20,
          backgroundColor: statusColor,
          borderRadius: "50% 50% 50% 0",
          transform: "rotate(-45deg)",
          mb: 1,
          boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%) rotate(45deg)",
            width: 6,
            height: 6,
            backgroundColor: "white",
            borderRadius: "50%",
          }}
        />
      </Box>
      <Box
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          color: statusColor,
          fontSize: 12,
          fontWeight: "bold",
          px: 1,
          py: 0.5,
          borderRadius: 1,
          whiteSpace: "nowrap",
          maxWidth: 120,
          overflow: "visible",
          textOverflow: "ellipsis",
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
          border: `1px solid ${statusColor}`,
          textAlign: "center",
        }}
      >
        {workerName}
      </Box>
    </Box>
  );
};

const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  const localDateTime = convertUTCToLocalDateTime(dateString, { locale: "en-GB" });
  return localDateTime === "—" ? "-" : localDateTime;
};

const MapTask = ({
  selectedTaskId,
  onClearSelection,
  onSelectTask,
  appliedFilters = {}
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:768px)");
  const rawEncryptedToken = localStorage.getItem("token");
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFullMapOpen, setIsFullMapOpen] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState(null);
  const previewMapRef = useRef(null);
  const fullMapRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    let currentPage = 1;
    const allMarkers = [];

    const fetchData = async () => {
      try {
        let hasNextPage = true;
        while (hasNextPage) {
          const res = await fetch(`${BASE_URL}/task-management/worker/tasks?page=${currentPage}&page_size=50&sort=created_at:desc`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
          const data = await res.json();

          if (!data.error && data.data?.tasks) {
            data.data.tasks.forEach((task) => {
              if (task.location_lat && task.location_lng) {
                const assignedWorkerNames = task.assigned_workers
                  ? task.assigned_workers.map((worker) => worker.worker_name).join(", ")
                  : task.assigned_by_name || "Unknown Worker";

                allMarkers.push({
                  id: task.id,
                  lat: parseFloat(task.location_lat),
                  lng: parseFloat(task.location_lng),
                  title: task.title,
                  description: task.description,
                  status: task.status,
                  priority: task.priority,
                  startAt: task.start_at,
                  endAt: task.end_at,
                  locationAddress: task.location_address,
                  assignedBy: task.assigned_by_name,
                  assignedWorkers: assignedWorkerNames,
                  assignedWorkersCount: task.assigned_workers_count,
                  completedWorkersCount: task.completed_workers_count,
                  locationRadiusM: task.location_radius_m,
                });
              }
            });

            const pagination = data.data.pagination || {};
            hasNextPage = pagination.has_next || false;
            currentPage += 1;
          } else {
            hasNextPage = false;
          }
        }

        console.log("Total task markers:", allMarkers.length);
        setMarkers(allMarkers);
      } catch (err) {
        console.error("Error fetching task map data:", err);
        setMarkers([]);
      } finally {
        setLoading(false);
      }
    };

    const reload = () => fetchData();
    fetchData();
    window.addEventListener("tasks:updated", reload);
    return () => window.removeEventListener("tasks:updated", reload);
  }, [token]);

  const displayedMarkers = useMemo(() => {
    const selectedMarkers = selectedTaskId
      ? markers.filter((m) => m.id === selectedTaskId)
      : markers;

    return selectedMarkers.filter((marker) => {
      const { status, startDate, endDate } = appliedFilters || {};

      if (status && marker.status !== status) return false;

      if (startDate) {
        const startBoundary = new Date(`${startDate}T00:00:00`);
        if (new Date(marker.startAt) < startBoundary) return false;
      }

      if (endDate) {
        const endBoundary = new Date(`${endDate}T23:59:59`);
        if (new Date(marker.endAt) > endBoundary) return false;
      }

      return true;
    });
  }, [markers, selectedTaskId, appliedFilters]);

  const fitMapToMarkers = useCallback((map) => {
    if (!map || !window.google) return;

    const validMarkers = displayedMarkers.filter(
      (m) => typeof m.lat === "number" && typeof m.lng === "number"
    );

    if (validMarkers.length === 0) {
      map.setCenter(defaultCenter);
      map.setZoom(defaultZoom);
      return;
    }

    if (validMarkers.length === 1) {
      map.setCenter({ lat: validMarkers[0].lat, lng: validMarkers[0].lng });
      map.setZoom(focusedZoom);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    validMarkers.forEach((marker) => {
      bounds.extend({ lat: marker.lat, lng: marker.lng });
    });

    setTimeout(() => {
      try {
        map.fitBounds(bounds, 50);
      } catch (err) {
        console.error("fitBounds error:", err);
      }
    }, 100);
  }, [displayedMarkers]);

  useEffect(() => {
    fitMapToMarkers(previewMapRef.current);
    fitMapToMarkers(fullMapRef.current);
  }, [fitMapToMarkers, isFullMapOpen]);

  useEffect(() => {
    if (activeTaskId && !displayedMarkers.some((task) => task.id === activeTaskId)) {
      setActiveTaskId(null);
    }
  }, [displayedMarkers, activeTaskId]);

  const handleTaskSelect = (task) => {
    setActiveTaskId(task.id);
    onSelectTask && onSelectTask(task.id);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 350 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%", height: isMobile ? 300 : 400 }}>
      <CustomText
        sx={{
          ...textStyles.h1,
          fontWeight: 600,
          fontSize: "18px",
          color: "#003149",
          textAlign: "left",
          mb: 2,
        }}
      >
        {t("liveTask")}
      </CustomText>
      <GoogleMap
        center={displayedMarkers[0] ? { lat: displayedMarkers[0].lat, lng: displayedMarkers[0].lng } : defaultCenter}
        zoom={displayedMarkers.length > 0 ? focusedZoom : defaultZoom}
        mapContainerStyle={{ width: "100%", height: "100%", borderRadius: 8 }}
        options={{ streetViewControl: false, mapTypeControl: false }}
        onLoad={(map) => {
          previewMapRef.current = map;
          fitMapToMarkers(map);
        }}
      >
        {displayedMarkers.map((task) => (
          <React.Fragment key={task.id}>
            <Circle
              center={{ lat: task.lat, lng: task.lng }}
              radius={task.locationRadiusM || 200}
              options={{
                fillColor: statusColors[task.status] || statusColors.assigned,
                strokeColor: statusColors[task.status] || statusColors.assigned,
                strokeWeight: 2,
                strokeOpacity: 0.3,
                fillOpacity: 0.1,
              }}
            />
            <OverlayView
              position={{ lat: task.lat, lng: task.lng }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={(width, height) => ({
                x: -(width / 2),
                y: -height,
              })}
            >
              <Box onClick={() => handleTaskSelect(task)}>
                <TaskMarkerContent workerName={task.assignedWorkers} status={task.status} />
              </Box>
            </OverlayView>
            {activeTaskId === task.id && (
              <InfoWindow
                position={{ lat: task.lat, lng: task.lng }}
                onCloseClick={() => setActiveTaskId(null)}
              >
                <div style={{ minWidth: 200 }}>
                  <h3
                    style={{
                      margin: "0 0 8px 0",
                      color: statusColors[task.status] || statusColors.assigned,
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    onClick={() => onSelectTask && onSelectTask(task.id)}
                  >
                    {task.title}
                  </h3>
                  <p
                    style={{
                      margin: "4px 0",
                      fontSize: "14px",
                      color: "#333",
                      fontWeight: "bold",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    onClick={() => onSelectTask && onSelectTask(task.id)}
                  >
                    👤 {task.assignedWorkers}
                  </p>
                  <p style={{ margin: "4px 0", fontSize: "12px", color: "#666" }}>
                    <strong>Status:</strong> {task.status.replace("_", " ").toUpperCase()}
                  </p>
                  <p style={{ margin: "4px 0", fontSize: "12px", color: "#666" }}>
                    <strong>Priority:</strong> {task.priority}
                  </p>
                  <p style={{ margin: "4px 0", fontSize: "12px", color: "#666" }}>
                    <strong>Workers:</strong> {task.completedWorkersCount}/{task.assignedWorkersCount} completed
                  </p>
                  <p style={{ margin: "4px 0", fontSize: "12px", color: "#666" }}>
                    <strong>Start:</strong> {formatDateTime(task.startAt)}
                  </p>
                  <p style={{ margin: "4px 0", fontSize: "12px", color: "#666" }}>
                    <strong>End:</strong> {formatDateTime(task.endAt)}
                  </p>
                  <p style={{ margin: "4px 0", fontSize: "11px", color: "#999", wordBreak: "break-word" }}>
                    📍 {task.locationAddress}
                  </p>
                </div>
              </InfoWindow>
            )}
          </React.Fragment>
        ))}
      </GoogleMap>
      {selectedTaskId && (
        <IconButton
          onClick={onClearSelection}
          sx={{
            position: "absolute",
            top: 35,
            right: 12,
            zIndex: 1000,
            backgroundColor: "white",
            boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
            "&:hover": { backgroundColor: "#f5f5f5" },
          }}
        >
          <CloseIcon sx={{ color: "#d32f2f" }} />
        </IconButton>
      )}
      <CustomButton
        onClick={() => setIsFullMapOpen(true)}
        size="small"
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          backgroundColor: "#006EC2",
          color: "white",
          fontSize: "12px",
          padding: "4px 10px",
          borderRadius: "6px",
          zIndex: 1000,
        }}
        label={
          <>
            {t("dashboard.viewFullMap")}
            <Box component="img" src={maparrow} alt="view icon" sx={{ ml: 1, width: 15, height: 13, objectFit: "contain" }} />
          </>
        }
      />

      {isFullMapOpen && (
        <Box sx={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: 2000, backgroundColor: "white" }}>
          <IconButton
            onClick={() => setIsFullMapOpen(false)}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 2100,
              backgroundColor: "white",
              "&:hover": { backgroundColor: "white" },
            }}
          >
            <CloseIcon sx={{ color: "red" }} />
          </IconButton>

          <GoogleMap
            center={displayedMarkers[0] ? { lat: displayedMarkers[0].lat, lng: displayedMarkers[0].lng } : defaultCenter}
            zoom={displayedMarkers.length > 0 ? focusedZoom : defaultZoom}
            mapContainerStyle={{ width: "100%", height: "100%", borderRadius: 8 }}
            options={{ streetViewControl: false, mapTypeControl: false }}
            onLoad={(map) => {
              fullMapRef.current = map;
              fitMapToMarkers(map);
            }}
          >
            {displayedMarkers.map((task) => (
              <React.Fragment key={task.id}>
                <Circle
                  center={{ lat: task.lat, lng: task.lng }}
                  radius={task.locationRadiusM || 200}
                  options={{
                    fillColor: statusColors[task.status] || statusColors.assigned,
                    strokeColor: statusColors[task.status] || statusColors.assigned,
                    strokeWeight: 2,
                    strokeOpacity: 0.3,
                    fillOpacity: 0.1,
                  }}
                />
                <OverlayView
                  position={{ lat: task.lat, lng: task.lng }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  getPixelPositionOffset={(width, height) => ({
                    x: -(width / 2),
                    y: -height,
                  })}
                >
                  <Box onClick={() => handleTaskSelect(task)}>
                    <TaskMarkerContent workerName={task.assignedWorkers} status={task.status} />
                  </Box>
                </OverlayView>
                {activeTaskId === task.id && (
                  <InfoWindow
                    position={{ lat: task.lat, lng: task.lng }}
                    onCloseClick={() => setActiveTaskId(null)}
                  >
                    <div style={{ minWidth: 200 }}>
                      <h3
                        style={{
                          margin: "0 0 8px 0",
                          color: statusColors[task.status] || statusColors.assigned,
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                        onClick={() => onSelectTask && onSelectTask(task.id)}
                      >
                        {task.title}
                      </h3>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "14px",
                          color: "#333",
                          fontWeight: "bold",
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                        onClick={() => onSelectTask && onSelectTask(task.id)}
                      >
                        👤 {task.assignedWorkers}
                      </p>
                      <p style={{ margin: "4px 0", fontSize: "12px", color: "#666" }}>
                        <strong>Status:</strong> {task.status.replace("_", " ").toUpperCase()}
                      </p>
                      <p style={{ margin: "4px 0", fontSize: "12px", color: "#666" }}>
                        <strong>Priority:</strong> {task.priority}
                      </p>
                      <p style={{ margin: "4px 0", fontSize: "12px", color: "#666" }}>
                        <strong>Workers:</strong> {task.completedWorkersCount}/{task.assignedWorkersCount} completed
                      </p>
                      <p style={{ margin: "4px 0", fontSize: "12px", color: "#666" }}>
                        <strong>Start:</strong> {formatDateTime(task.startAt)}
                      </p>
                      <p style={{ margin: "4px 0", fontSize: "12px", color: "#666" }}>
                        <strong>End:</strong> {formatDateTime(task.endAt)}
                      </p>
                      <p style={{ margin: "4px 0", fontSize: "11px", color: "#999", wordBreak: "break-word" }}>
                        📍 {task.locationAddress}
                      </p>
                    </div>
                  </InfoWindow>
                )}
              </React.Fragment>
            ))}
          </GoogleMap>
        </Box>
      )}
    </Box>
  );
};

export default MapTask;
